import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Button, Dialog, DialogFooter, Label } from '@/shared/ui';
import { getUserDisplayName } from '@entities/User';
import type { User } from '@psp-aggregator/sdk';

export interface RejectUserModalProps {
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  submitting?: boolean;
  user: User;
}

export const RejectUserModal: FC<RejectUserModalProps> = ({
  onClose,
  onConfirm,
  submitting = false,
  user,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  return (
    <Dialog title={t('pages.admin.users.modals.rejectTitle')} onClose={onClose}>
      <div className="mb-3 space-y-1">
        <p className="text-sm font-medium">{getUserDisplayName(user)}</p>
        <p className="text-xs text-muted-foreground">{user.email}</p>
      </div>

      <Label htmlFor="reject-reason">
        {t('pages.admin.users.modals.rejectReason')}
      </Label>
      <textarea
        className="mt-1.5 w-full resize-none rounded-xl border border-border bg-background/60 px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        id="reject-reason"
        placeholder={t('pages.admin.users.modals.rejectReasonPlaceholder')}
        rows={3}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />

      <DialogFooter
        className="mt-4"
        endSlot={
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('buttons.cancel')}
            </Button>
            <Button
              disabled={submitting}
              type="button"
              variant="destructive"
              onClick={() => {
                void onConfirm(reason);
              }}>
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('buttons.reject')
              )}
            </Button>
          </>
        }
      />
    </Dialog>
  );
};

RejectUserModal.displayName = 'RejectUserModal';
